/* LAV TDMS — Dashboard / project overview. window.APP.Dashboard */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { useList, useMeta, useSub } from '@/data/queries'
import { useDictLabels, useMe } from '@/data/meta'
import type { AuditEntry, Mdr, Project } from '@/data/types'
import { exportDashboard } from '@/lib/excel'
import { Icon } from '@/ds/icons'
import {
  Card,
  Button,
  Select,
  PageHead,
  KPIStat,
  CompletionBar,
  DiscTag,
  Spinner,
} from '@/ui'
import { t, type Lang } from '@/i18n/lang'


/* Формы витрин обзора (services/core → DashboardController). Все числа считает
   сервер: у экрана нет ни одного показателя, выведенного из памяти вкладки. */
interface KpiTile {
  /** null, когда показателя нет в данных, — плитка тогда пишет «—», а не ноль. */
  value: number | null
  /** Динамика к предыдущему окну той же длины; null — сравнивать не с чем. */
  delta: string | null
}
interface Kpi {
  received: KpiTile
  review: KpiTile
  overdue: KpiTile
  cycle: KpiTile
  /** Время ответа подрядчика на замечание — не то же, что оборачиваемость. */
  answerTime: KpiTile
  iterations: KpiTile
}
/** Точка S-кривой: накопленные количества документов, не проценты. */
interface CurvePoint { label: string; plan: number; forecast: number; fact: number; past: boolean }
interface Curve { periods: CurvePoint[]; total: number; currentIndex: number }
interface ThroughputPoint { label: string; in: number; out: number }
interface OverdueRow { no: string; title: string; disc: string; vendor: string; life: string; plan: string; forecast: string; days: number }

/** Состояние панели. Пустая панель молчит, а «нет данных» и «не загрузилось» — разное. */
function State({ q, lang, err }: { q: { isPending: boolean; isError: boolean }; lang: Lang; err: string }) {
  if (q.isError) return <div style={{ padding: 14, font: 'var(--font-ui)', color: 'var(--danger-ink)' }}>{err}</div>
  if (q.isPending) return <div style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', color: 'var(--text-subtle)' }}><Spinner size={13} />{t(lang, 'Загрузка…', 'Loading…')}</div>
  return null
}

function Metric({ label, value, c }: { label: string; value: string; c: string }) {
  return (
    <div>
      <div style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', marginBottom: 3 }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 20, fontWeight: 600, color: `var(${c})` }}>{value}</div>
    </div>
  )
}

/* Кривая рисуется по рядам сервера, точка за точкой. Общий SCurve из @/ui
   принимает только конечные значения и достраивает форму сглаживанием —
   промежуточные месяцы там выдуманы, а выдуманный график выпуска показывать
   нельзя. Проценты берутся от общего числа документов набора. */
function SCurveChart({ periods, currentIndex, total, height = 230 }: { periods: CurvePoint[]; currentIndex: number; total: number; height?: number }) {
  const ref = useRef<HTMLDivElement>(null)
  const [w, setW] = useState(660)
  /* Ширина снимается до отрисовки. Пассивный эффект React выполняет уже после
     кадра, поэтому первый кадр показывал кривую в 660 px и она прыгала на
     настоящую ширину. Повторялось при каждой смене реестра: ключ запроса
     меняется, данных нет, график размонтируется и рождается снова с 660. */
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    setW(el.clientWidth)
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((es) => {
      for (const e of es) setW(e.contentRect.width)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const n = periods.length
  const padL = 30
  const padR = 14
  const padT = 12
  const padB = 22
  const cw = Math.max(120, w - padL - padR)
  const ch = height - padT - padB
  const X = (i: number) => padL + (n > 1 ? (i / (n - 1)) * cw : cw / 2)
  const Y = (v: number) => padT + (1 - v / 100) * ch
  const pct = (v: number) => (total ? (v / total) * 100 : 0)
  const plan = periods.map((p) => pct(p.plan))
  const fcst = periods.map((p) => pct(p.forecast))
  /* Факт — только по прошедшим месяцам. Дальше накопленное количество равно
     сегодняшнему, и продолженная линия читалась бы как выполненный план. */
  const fact = periods.map((p, i) => (i <= currentIndex ? pct(p.fact) : null))
  const lineOf = (arr: (number | null)[]) =>
    arr
      .map((v, i) => (v == null ? null : (i && arr[i - 1] != null ? 'L' : 'M') + X(i) + ' ' + Y(v)))
      .filter(Boolean)
      .join(' ')
  const factPts = fact.filter((v): v is number => v != null)
  const factArea = factPts.length
    ? `M ${X(0)} ${Y(0)} ` + factPts.map((v, i) => 'L ' + X(i) + ' ' + Y(v)).join(' ') + ` L ${X(factPts.length - 1)} ${Y(0)} Z`
    : ''
  const here = periods[currentIndex]
  return (
    <div ref={ref} style={{ width: '100%' }}>
      <svg width={w} height={height} style={{ display: 'block' }}>
        {[0, 25, 50, 75, 100].map((g) => (
          <g key={g}>
            <line x1={padL} y1={Y(g)} x2={padL + cw} y2={Y(g)} stroke="var(--border-subtle)" strokeWidth="1" />
            <text x={padL - 6} y={Y(g) + 3} textAnchor="end" fontSize="9" fill="var(--text-subtle)" fontFamily="var(--font-mono)">{g}</text>
          </g>
        ))}
        {periods.map((p, i) => (i % (n > 8 ? 2 : 1) === 0 ? <text key={p.label} x={X(i)} y={height - 6} textAnchor="middle" fontSize="9" fill="var(--text-subtle)" fontFamily="var(--font-mono)">{p.label}</text> : null))}
        <line x1={X(currentIndex)} y1={padT} x2={X(currentIndex)} y2={padT + ch} stroke="var(--accent)" strokeWidth="1" strokeDasharray="3 3" />
        <text x={X(currentIndex)} y={padT - 2} textAnchor="middle" fontSize="8.5" fill="var(--accent-ink)" fontFamily="var(--font-mono)">сегодня</text>
        <path d={factArea} fill="var(--green-500)" opacity="0.1" />
        <path d={lineOf(plan)} fill="none" stroke="var(--slate-400)" strokeWidth="1.6" strokeDasharray="5 4" />
        <path d={lineOf(fcst)} fill="none" stroke="var(--blue-500)" strokeWidth="1.8" />
        <path d={lineOf(fact)} fill="none" stroke="var(--green-500)" strokeWidth="2.4" />
        {here ? <circle cx={X(currentIndex)} cy={Y(pct(here.fact))} r="3.5" fill="var(--green-500)" stroke="var(--surface-card)" strokeWidth="1.5" /> : null}
      </svg>
      <div style={{ display: 'flex', gap: 16, paddingLeft: padL, marginTop: 4 }}>
        <LegendDot color="--slate-400" dash label="План" />
        <LegendDot color="--blue-500" label="Прогноз" />
        <LegendDot color="--green-500" label="Факт" />
      </div>
    </div>
  )
}

function LegendDot({ color, dash, label }: { color: string; dash?: boolean; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
      <span style={{ width: 14, height: 0, borderTop: `2px ${dash ? 'dashed' : 'solid'} var(${color})` }} />{label}
    </span>
  )
}

function SCurveCard({ lang }: { lang: Lang }) {
  /* Разрез один на кривую: «mdr:КОД» — партия подачи подрядчика, «wp:КОД» —
     состав работ. Две независимые выпадашки давали бы пересечение двух
     разрезов, а объяснить получившуюся кривую было бы нечем. */
  const [sel, setSel] = useState('all')
  // Реестры — для выбора области кривой; сама кривая считается на сервере,
  // потому что она строится по всем документам набора, а не по странице.
  const mdrsQuery = useList<Mdr>('mdrs', { page: 0, pageSize: 200, sort: [{ id: 'id', desc: false }] })
  const packagesQuery = useSub<{ packages: { code: string; title: string }[] }>('/work-packages')
  const cut = sel === 'all' ? ''
    : sel.startsWith('wp:') ? '?wp=' + encodeURIComponent(sel.slice(3))
    : '?mdr=' + encodeURIComponent(sel.slice(4))
  const curveQuery = useSub<Curve>('/dashboard/scurve' + cut)
  const curve = curveQuery.data
  const point = curve?.periods[curve.currentIndex]
  const total = curve?.total ?? 0
  const pct = (v: number) => (total ? Math.round((v / total) * 100) : 0)
  // Метрики справа — значения рядов в текущем месяце. Раньше «план» был
  // литералом 70%, и отклонение считалось от него.
  const factPct = point ? pct(point.fact) : null
  const planPct = point ? pct(point.plan) : null
  const gap = factPct != null && planPct != null ? factPct - planPct : null
  return (
    <Card title={t(lang, 'S-кривая выпуска документации', 'Document S-curve')} subtitle={t(lang, 'Накопленный выпуск: план / прогноз / факт', 'Cumulative issue: plan / forecast / actual')}
      actions={<Select size="sm" value={sel} onChange={(e) => setSel(e.target.value)} block={false} style={{ width: 260 }}>
        <option value="all">{t(lang, 'Весь проект', 'Whole project')}</option>
        <optgroup label={t(lang, 'Реестр MDR', 'MDR register')}>
          {(mdrsQuery.data?.rows ?? []).map((m) => <option key={m.id} value={'mdr:' + m.id}>{m.id} — {m.title}</option>)}
        </optgroup>
        {(packagesQuery.data?.packages ?? []).length ? (
          <optgroup label={t(lang, 'Пакет работ', 'Work package')}>
            {(packagesQuery.data?.packages ?? []).map((w) => (
              <option key={w.code} value={'wp:' + w.code}>{w.code} — {w.title}</option>
            ))}
          </optgroup>
        ) : null}
      </Select>}>
      {/* Заглушка держит высоту графика. Без неё сообщение в одну строку
          вместо блока в ~254 px подбрасывало всё, что ниже, — и повторно при
          каждой смене реестра, а не только на первой загрузке. */}
      {!curve ? <div style={{ height: 254 }}><State q={curveQuery} lang={lang} err={t(lang, 'Не удалось загрузить S-кривую', 'Failed to load the S-curve')} /></div> : (
        <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: 0 }}><SCurveChart periods={curve.periods} currentIndex={curve.currentIndex} total={total} height={230} /></div>
          <div style={{ width: 150, flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: 12, borderLeft: '1px solid var(--border-subtle)', paddingLeft: 18 }}>
            <Metric label={t(lang, 'Факт на сегодня', 'Actual to date')} value={factPct == null ? '—' : factPct + '%'} c="--green-600" />
            <Metric label={t(lang, 'План на сегодня', 'Plan to date')} value={planPct == null ? '—' : planPct + '%'} c="--slate-500" />
            <Metric label={t(lang, 'Отклонение', 'Variance')} value={gap == null ? '—' : gap + ' п.п.'} c={gap == null ? '--text-subtle' : gap < 0 ? '--danger-ink' : '--green-600'} />
          </div>
        </div>
      )}
    </Card>
  )
}

function Legend({ color, label }: { color: string; label: string }) {
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}><span style={{ width: 10, height: 10, background: `var(${color})`, borderRadius: 1 }} />{label}</span>
}

export function Dashboard({ lang = 'ru', onNav, onOpenDoc }: { lang?: Lang; onNav: (v: string) => void; onOpenDoc: (no: string) => void }) {
  // Окно показателей — параметр запроса: раньше список был декоративным и на
  // цифры не влиял.
  const [period, setPeriod] = useState('30')
  const kpiQuery = useSub<Kpi>('/dashboard/kpi?period=' + period)
  const mdrsQuery = useList<Mdr>('mdrs', { page: 0, pageSize: 200, sort: [{ id: 'id', desc: false }] })
  const throughputQuery = useSub<ThroughputPoint[]>('/dashboard/throughput?weeks=8')
  /* Карточка показывает пять строк — столько и запрашивается. Общее число
     просроченных берётся из плитки KPI: там оно не ограничено выборкой. */
  const overdueQuery = useSub<OverdueRow[]>('/dashboard/overdue?limit=5')
  const meQuery = useMe()
  const me = meQuery.data
  /* Журнал — свой раздел прав (V64), и у координатора ДО его нет. Лента
     запрашивала журнал всё равно, получала 403 и писала «не удалось загрузить
     ленту активности», а ссылка вела на закрытый экран с красной ошибкой:
     отказ по правам выглядел поломкой сервера. Спрашиваем только там, где
     право есть, и об отказе говорим словом. */
  const mayAudit = (me?.perms?.audit ?? '—') !== '—'
  const auditQuery = useList<AuditEntry>('audit', { page: 0, pageSize: 7, sort: [{ id: 'ts', desc: true }] },
                                         { enabled: mayAudit })
  const project = useMeta<Project[]>('projects').data?.find((p) => p.id === me?.project)
  const discLabels = useDictLabels('disc')

  const K = kpiQuery.data
  /* Пока показатель не пришёл (или его нет в данных) — «—», а не ноль: ноль
     читался бы как достоверный ноль. */
  const shown = (tile?: KpiTile): ReactNode => (tile && tile.value != null ? tile.value : '—')
  const mdrs = mdrsQuery.data?.rows ?? []
  const overdue = overdueQuery.data ?? []
  const throughput = throughputQuery.data ?? []

  const actIcon: Record<string, string> = { register: 'inbox', assign: 'user-check', comment: 'message-square', approve: 'check-check', upload: 'upload', issue: 'send', login: 'log-in' }
  const actColor: Record<string, string> = { register: '--blue-500', assign: '--amber-400', comment: '--yellow-600', approve: '--green-500', upload: '--blue-500', issue: '--amber-400', login: '--slate-400' }
  // Единица шкалы столбиков, а не показатель: не даёт делить на ноль на пустой неделе.
  const maxTh = Math.max(1, ...throughput.flatMap((x) => [x.in, x.out]))

  /* Выгрузка обзора. Просрочка берётся полнее, чем показано карточкой (на
     экране пять строк, в файле до двухсот): файл открывают, чтобы работать со
     списком, а не чтобы прочитать те же пять строк. S-кривая берётся по всему
     проекту — тот же запрос, что у панели без выбранного реестра, поэтому
     повторного обращения к серверу не будет. */
  const overdueFull = useSub<OverdueRow[]>('/dashboard/overdue?limit=200')
  const curveAll = useSub<Curve>('/dashboard/scurve')
  const exportOverview = () => exportDashboard({
    period: t(lang, `За ${period} дней`, `Last ${period} days`),
    kpi: [
      [t(lang, 'Поступило', 'Received'), K?.received.value ?? '—'],
      [t(lang, 'На рассмотрении', 'In review'), K?.review.value ?? '—'],
      [t(lang, 'Просрочено', 'Overdue'), K?.overdue.value ?? '—'],
      [t(lang, 'Оборачиваемость, дн', 'Turnaround, days'), K?.cycle.value ?? '—'],
      [t(lang, 'Ответ подрядчика, дн', 'Contractor response, days'), K?.answerTime.value ?? '—'],
      [t(lang, 'Итераций на документ', 'Iterations per document'), K?.iterations.value ?? '—'],
    ],
    curve: (curveAll.data?.periods ?? []).map((p) => ({ label: p.label, plan: p.plan, forecast: p.forecast, fact: p.fact })),
    throughput,
    overdue: (overdueFull.data ?? overdue) as unknown as Record<string, unknown>[],
  }, 'dashboard.xlsx', lang)

  return (
    <div style={{ minHeight: '100%', background: 'var(--surface-page)' }}>
      <PageHead
        crumbs={<span>{me?.project ?? ''} <span style={{ color: 'var(--border-strong)' }}>/</span> {t(lang, 'Обзор', 'Overview')}</span>}
        title={project?.name ?? ''}
        sub={project?.plant ?? ''}
        actions={<>
          <Select size="sm" value={period} onChange={(e) => setPeriod(e.target.value)} style={{ width: 150 }}><option value="7">{t(lang, 'За 7 дней', 'Last 7 days')}</option><option value="30">{t(lang, 'За 30 дней', 'Last 30 days')}</option><option value="90">{t(lang, 'За квартал', 'Quarter')}</option></Select>
          <Button variant="secondary" size="sm" iconLeft={<Icon name="download" size={14} />}
                  disabled={!K} title={!K ? t(lang, 'Показатели ещё не загружены', 'KPIs are still loading') : undefined}
                  onClick={exportOverview}>{t(lang, 'Экспорт', 'Export')}</Button>
        </>}
      />

      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* KPI row */}
        {kpiQuery.isError ? <div style={{ font: 'var(--font-ui)', color: 'var(--danger-ink)' }}>{t(lang, 'Не удалось загрузить показатели', 'Failed to load the KPIs')}</div> : null}
        <div style={{ display: 'flex', gap: 12 }}>
          <KPIStat icon="inbox" label={t(lang, 'Документов получено', 'Received')} value={shown(K?.received)} delta={K?.received.delta ?? undefined} tone="up" accent />
          <KPIStat icon="clipboard-check" label={t(lang, 'На рассмотрении', 'In review')} value={shown(K?.review)} delta={K?.review.delta ?? undefined} tone="warn" />
          <KPIStat icon="circle-alert" label={t(lang, 'Просрочено', 'Overdue')} value={shown(K?.overdue)} delta={K?.overdue.delta ?? undefined} tone="down" />
          {/* Оборачиваемость: от подачи подрядчика до возвратного письма — то,
              за что отвечает сторона заказчика. Время ответа подрядчика рядом:
              это другая величина и другой вопрос. */}
          <KPIStat icon="timer" label={t(lang, 'Оборачиваемость рассмотрения', 'Review turnaround')} value={shown(K?.cycle)} unit={t(lang, 'дн', 'd')} delta={K?.cycle.delta ?? undefined} tone="up" />
          <KPIStat icon="message-square-reply" label={t(lang, 'Ответ подрядчика', 'Contractor response')} value={shown(K?.answerTime)} unit={t(lang, 'дн', 'd')} tone="up" />
          {/* Целевого значения итераций в данных нет — плитка показывает только факт. */}
          <KPIStat icon="repeat" label={t(lang, 'Итераций / док.', 'Iterations / doc')} value={shown(K?.iterations)} delta={K?.iterations.delta ?? undefined} tone="neutral" />
        </div>

        <SCurveCard lang={lang} />

        <div style={{ display: 'grid', gridTemplateColumns: '1.55fr 1fr', gap: 18, alignItems: 'start' }}>
          {/* Left column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <Card title={t(lang, 'Комплектность по реестрам MDR', 'MDR completion')} actions={<Button variant="ghost" size="sm" iconRight={<Icon name="arrow-right" size={14} />} onClick={() => onNav('mdr')}>{t(lang, 'Все реестры', 'All registers')}</Button>} padding={false}>
              <div style={{ padding: '6px 0' }}>
                <State q={mdrsQuery} lang={lang} err={t(lang, 'Не удалось загрузить реестры', 'Failed to load registers')} />
                {mdrs.map((m) => {
                  const pct = Math.round((m.afd / m.docs) * 100)
                  return (
                    <button key={m.id} onClick={() => onNav('mdr')} className="tdms-rail" style={{ display: 'grid', gridTemplateColumns: '210px 1fr 56px', alignItems: 'center', gap: 14, width: '100%', padding: '11px 16px', border: 0, borderBottom: '1px solid var(--border-subtle)', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>{m.disc ? <DiscTag code={m.disc} /> : null}<span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>{m.id}</span></div>
                        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.title}</div>
                      </div>
                      <div>
                        <CompletionBar afd={m.afd} ifa={m.ifa} ifr={m.ifr} planned={m.planned} />
                        <div style={{ display: 'flex', gap: 12, marginTop: 6, fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-subtle)' }}>
                          <span style={{ color: 'var(--green-600)' }}>AFD {m.afd}</span>
                          <span style={{ color: 'var(--blue-600)' }}>IFA {m.ifa}</span>
                          <span style={{ color: 'var(--accent-ink)' }}>IFR {m.ifr}</span>
                          {m.overdue > 0 ? <span style={{ color: 'var(--danger-ink)', marginLeft: 'auto' }}>● {m.overdue} {t(lang, 'проср.', 'ovd')}</span> : null}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}><span style={{ fontFamily: 'var(--font-mono)', fontSize: 17, fontWeight: 600, color: 'var(--text-strong)' }}>{pct}%</span></div>
                    </button>
                  )
                })}
              </div>
            </Card>

            <Card title={t(lang, 'Поток документов (вход / возврат)', 'Throughput (in / returned)')} padding={true}>
              {!throughputQuery.isSuccess ? <State q={throughputQuery} lang={lang} err={t(lang, 'Не удалось загрузить поток документов', 'Failed to load the throughput')} /> : (
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 18, height: 150, padding: '8px 4px 0' }}>
                  {throughput.map((w) => (
                    <div key={w.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 110, width: '100%', justifyContent: 'center' }}>
                        <span title={'Вход ' + w.in} style={{ width: 16, height: ((w.in / maxTh) * 100) + '%', background: 'var(--accent-400, var(--amber-400))', borderRadius: '1px 1px 0 0' }} />
                        <span title={'Возврат ' + w.out} style={{ width: 16, height: ((w.out / maxTh) * 100) + '%', background: 'var(--blue-500)', borderRadius: '1px 1px 0 0' }} />
                      </div>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-subtle)' }}>{w.label}</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 18, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
                <Legend color="--amber-400" label={t(lang, 'Получено (вх. TRM)', 'Received')} />
                <Legend color="--blue-500" label={t(lang, 'Возвращено (исх. TRM + CRS)', 'Returned')} />
              </div>
            </Card>
          </div>

          {/* Right column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <Card title={t(lang, 'Сроки под контролем', 'Deadlines')} actions={<span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--danger-ink)' }}>{shown(K?.overdue)} {t(lang, 'проср.', 'ovd')}</span>} padding={false}>
              <div>
                <State q={overdueQuery} lang={lang} err={t(lang, 'Не удалось загрузить просроченные документы', 'Failed to load overdue documents')} />
                {overdue.map((d) => (
                  <button key={d.no} onClick={() => onOpenDoc(d.no)} className="tdms-rail" style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', border: 0, borderBottom: '1px solid var(--border-subtle)', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}>
                    <span style={{ color: 'var(--danger-ink)', display: 'inline-flex' }}><Icon name="flag" size={14} /></span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-strong)' }}>{d.no}</div>
                      <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.title}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {/* Назначения ревизии витрина не отдаёт — показывается дисциплина строки. */}
                      <span title={discLabels[d.disc] ?? d.disc}><DiscTag code={d.disc} /></span>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--danger-ink)', marginTop: 3 }}>→ {d.forecast}</div>
                    </div>
                  </button>
                ))}
              </div>
            </Card>

            <Card title={t(lang, 'Лента активности', 'Activity')} padding={false}>
              <div style={{ padding: '6px 0' }}>
                {/* Пока права неизвестны, отказ показывать рано: он мигнул бы на
                    каждой загрузке экрана у всех подряд. */}
                {!meQuery.isSuccess
                  ? <div style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', color: 'var(--text-subtle)' }}><Spinner size={13} />{t(lang, 'Загрузка…', 'Loading…')}</div>
                  : mayAudit
                    ? <State q={auditQuery} lang={lang} err={t(lang, 'Не удалось загрузить ленту активности', 'Failed to load the activity feed')} />
                    : <div style={{ padding: 14, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
                        {t(lang, 'Доступ запрещён: журнал событий открыт не каждой роли. Право выдаёт администратор проекта.',
                                 'Access denied: the event log is not open to every role. The project administrator grants the right.')}
                      </div>}
                {(auditQuery.data?.rows ?? []).map((a) => (
                  <div key={a.id} style={{ display: 'flex', gap: 11, padding: '9px 16px' }}>
                    <span style={{ display: 'grid', placeItems: 'center', width: 26, height: 26, flex: '0 0 auto', borderRadius: 'var(--radius-sm)', background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', color: `var(${actColor[a.action] ?? '--text-subtle'})` }}><Icon name={actIcon[a.action] ?? 'circle'} size={14} /></span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}><b style={{ fontWeight: 600, color: 'var(--text-strong)' }}>{a.user}</b> · <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{a.obj}</span></div>
                      <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>{a.target}</div>
                    </div>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-subtle)', whiteSpace: 'nowrap' }}>{a.ts.split(' ')[1]}</span>
                  </div>
                ))}
                {/* Ссылка в закрытый раздел вела к красной ошибке и была
                    единственным входом туда для этой роли: её просто нет. */}
                {mayAudit ? (
                  <button onClick={() => onNav('audit')} className="tdms-rail" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%', padding: '10px', border: 0, borderTop: '1px solid var(--border-subtle)', background: 'transparent', cursor: 'pointer', color: 'var(--text-link)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)' }}>{t(lang, 'Открыть журнал аудита', 'Open audit log')}<Icon name="arrow-right" size={13} /></button>
                ) : null}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
